import api from "../../utils/axios";

/**
 * One entry in the client picker. `id` is dbo.Users.SellerId — the id the activity report takes —
 * because GetSmallClientDetails_sp projects `u.SellerId as Id`.
 */
export interface SellerOption {
	id: number;
	firstName: string | null;
	lastName: string | null;
	username: string | null;
	uniqueSellerIdentifier: string | null;
}

/**
 * Source for the client picker.
 *
 * Reuses `User/select-small-client-details`, the same endpoint FilterOrderSection,
 * FilterSettlementsSection and SettlementRefusal already use for their client pickers, rather
 * than `User/sellers`: that one runs GetClients_sp, which does two full per-seller aggregates
 * over dbo.Orders and measured ~11s on a production-sized database — far too slow for a dropdown
 * that opens on page load. This SP touches Users, UserBusiness and Sellers only.
 *
 * The SP groups by `u.Id` while projecting `u.SellerId`, so one seller can come back more than
 * once. Deduplicated here — the picker must not offer the same client twice, and the report keys
 * on SellerId anyway.
 */
export const getSellerOptions = async (): Promise<SellerOption[]> => {
	const response = await api.get<SellerOption[]>(
		"User/select-small-client-details"
	);

	const seen = new Set<number>();
	return (response.data ?? [])
		.filter((seller) => {
			if (!seller?.id || seen.has(seller.id)) {
				return false;
			}
			seen.add(seller.id);
			return true;
		})
		.sort((a, b) =>
			sellerLabel(a).localeCompare(sellerLabel(b), undefined, {
				sensitivity: "base",
			})
		);
};

/** "Emri Mbiemri (KODI)", falling back through username and id so a row is never blank. */
export const sellerLabel = (seller: SellerOption): string => {
	const name =
		`${seller.firstName ?? ""} ${seller.lastName ?? ""}`.trim() ||
		seller.username ||
		`#${seller.id}`;
	return seller.uniqueSellerIdentifier
		? `${name} (${seller.uniqueSellerIdentifier})`
		: name;
};

/**
 * What the picker's type-ahead matches against. Includes the numeric id so someone who already
 * has a SellerId from another report can still paste it straight in.
 */
export const sellerSearchText = (seller: SellerOption): string =>
	[
		seller.firstName,
		seller.lastName,
		seller.username,
		seller.uniqueSellerIdentifier,
		String(seller.id),
	]
		.filter(Boolean)
		.join(" ")
		.toLowerCase();
